/**
 * LyraMemoryManagement — Admin
 * Mémoire persistante de Lyra (informations injectées dans chaque conversation).
 * Pattern identique à OrionMemoryManagement, couleur amber.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Brain, Trash2, Loader, Plus } from 'lucide-react';
import { getLyraMemory, addLyraMemory, deleteLyraMemory } from '../../api';

const MEMORY_TYPES = [
  { value: 'learning',   label: 'Apprentissage', color: 'bg-blue-100 text-blue-700' },
  { value: 'context',    label: 'Contexte',      color: 'bg-amber-100 text-amber-700' },
  { value: 'preference', label: 'Préférence',    color: 'bg-green-100 text-green-700' },
  { value: 'error',      label: 'Erreur',        color: 'bg-red-100 text-red-700' },
];

const LyraMemoryManagement = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ type: 'context', content: '', source: 'manual' });
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);

  const fetchMemory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getLyraMemory();
      setEntries(res.data || []);
    } catch { setError('Erreur de chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMemory(); }, [fetchMemory]);

  const handleAdd = async () => {
    if (!form.content.trim()) return;
    setAdding(true);
    try {
      await addLyraMemory({ type: form.type, content: form.content.trim(), source: 'manual' });
      setForm({ type: 'context', content: '', source: 'manual' });
      setShowForm(false);
      await fetchMemory();
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur lors de l\'ajout');
    } finally { setAdding(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette entrée de mémoire ?')) return;
    try {
      await deleteLyraMemory(id);
      setEntries(prev => prev.filter(e => e.id !== id));
    } catch {}
  };

  const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-400';

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Brain size={16} className="text-amber-500" />
            Mémoire — Lyra ({entries.length} entrée{entries.length !== 1 ? 's' : ''})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-amber-50 rounded-lg px-4 py-3 text-sm text-amber-800 border border-amber-100">
            Les entrées de mémoire sont injectées dans chaque conversation de Lyra.
            Utilisez-les pour lui donner des informations persistantes sur votre structure ou ses comportements.
          </div>

          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-4 py-2 border border-dashed border-amber-300 rounded-xl text-sm text-amber-600 hover:bg-amber-50 w-full justify-center transition">
              <Plus size={16} />
              Ajouter une entrée
            </button>
          )}

          {showForm && (
            <div className="border border-amber-200 rounded-xl bg-amber-50/50 p-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                  className={inputCls}>
                  {MEMORY_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Contenu</label>
                <textarea rows={3} value={form.content}
                  onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                  className={inputCls + ' resize-none'}
                  placeholder="Ex: Notre structure suit les protocoles PMI du département du Rhône. La référente sanitaire est Claire Martin."
                />
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowForm(false)}
                  className="px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
                  Annuler
                </button>
                <button onClick={handleAdd} disabled={adding || !form.content.trim()}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-amber-500 hover:bg-amber-600 rounded-lg disabled:opacity-50">
                  {adding ? <Loader size={13} className="animate-spin" /> : <Plus size={13} />}
                  Enregistrer
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
          )}

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader className="animate-spin text-amber-500" size={24} />
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <Brain size={32} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">Aucune entrée en mémoire.</p>
              <p className="text-xs mt-1">Ajoutez des informations persistantes sur votre structure ou vos pratiques.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {entries.map(entry => {
                const typeStyle = MEMORY_TYPES.find(t => t.value === entry.type);
                return (
                  <div key={entry.id}
                    className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100 hover:bg-gray-100 transition group">
                    <span className={`flex-shrink-0 text-xs px-1.5 py-0.5 rounded-md font-medium ${typeStyle?.color || 'bg-gray-100 text-gray-600'}`}>
                      {typeStyle?.label || entry.type}
                    </span>
                    <p className="flex-1 text-sm text-gray-700 leading-relaxed">{entry.content}</p>
                    <div className="flex-shrink-0 flex flex-col items-end gap-1">
                      <button onClick={() => handleDelete(entry.id)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-gray-300 hover:text-red-500 transition rounded">
                        <Trash2 size={13} />
                      </button>
                      <span className="text-xs text-gray-300">
                        {new Date(entry.createdAt).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LyraMemoryManagement;
